import type { EntityStatus } from '@ms/yammer-data-support';
import { getStatus } from '@ms/yammer-data-support';
import createCachedSelector from 're-reselect';

import type { User, UserNetworkLeadersListFields, YammerState } from '../../state/types';
import { userNetworkLeadersListRequestStatusKey } from '../leadersManagement/actions.types';
import { getStatus as getRequestStatus } from '../requestStatuses/selectors';
import { getFulfilledUserNetworkLeaders } from '../userNetworkLeaders/selectors';
import { getFulfilledUsers } from '../users/selectors';

type GetLoadMoreUserNetworkLeadersListStatus = (state: YammerState) => EntityStatus;
export const getLoadMoreUserNetworkLeadersListStatus: GetLoadMoreUserNetworkLeadersListStatus = (state) =>
  getRequestStatus(state, 'UserNetworkLeadersListMore', userNetworkLeadersListRequestStatusKey);

type GetUserNetworkLeadersListStatus = (state: YammerState) => EntityStatus;
export const getUserNetworkLeadersListStatus: GetUserNetworkLeadersListStatus = (state) =>
  getStatus(state.userNetworkLeadersList);

const emptyUserNetworkLeaderIds: User['id'][] = [];
export const getUserNetworkLeadersListIdsFromState = (state: YammerState): User['id'][] =>
  state.userNetworkLeadersList.fulfilled?.userNetworkLeaders ?? emptyUserNetworkLeaderIds;

type GetUserNetworkLeadersList = (state: YammerState) => UserNetworkLeadersListFields[];
/**
 * @deprecated PERFORMANCE This selects a manually-constructed array of UserNetworkLeadersListFields objects,
 * which will return a new instance and force consumers to re-render whenever ANY user entity in the store gets
 * updated. This should be replaced by a `getUserNetworkLeadersIds` selector, and the top-level consuming component
 * can pass each id to a child component which will use `useUserField` or other "field" selectors for each individual
 * field it requires, while managing its own memoization to avoid unnecessary re-renders.
 */
export const getUserNetworkLeadersList: GetUserNetworkLeadersList = createCachedSelector(
  getUserNetworkLeadersListIdsFromState,
  getFulfilledUserNetworkLeaders,
  getFulfilledUsers,
  (userNetworkLeadersListIds, fulfilledUserNetworkLeaders, fulfilledUsers) =>
    userNetworkLeadersListIds.reduce<UserNetworkLeadersListFields[]>((acc, edgeId) => {
      const userNetworkLeader = fulfilledUserNetworkLeaders[edgeId];

      if (!userNetworkLeader) {
        throw new Error('Missing user network leader data in state for user network leaders list edge.');
      }

      const {
        id,
        hasNetworkWideAudience,
        viewerCanManageDirectAudience,
        viewerCanViewDirectAudienceMembers,
        directAudienceCount,
      } = userNetworkLeader;
      const { name, jobTitle, avatarUrlTemplate } = fulfilledUsers[id];

      acc.push({
        id,
        avatarUrlTemplate,
        name,
        jobTitle,
        hasNetworkWideAudience,
        viewerCanManageDirectAudience,
        viewerCanViewDirectAudienceMembers,
        directAudienceCount,
      });

      return acc;
    }, [])
)((_state) => 'cache');

type HasNextPageOfUserNetworkLeadersListAvailable = (state: YammerState) => boolean;
export const hasNextPageOfUserNetworkLeadersListAvailable: HasNextPageOfUserNetworkLeadersListAvailable = (state) =>
  state.userNetworkLeadersList.fulfilled?.hasNextPage ?? false;
